import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import styled from "styled-components";

import { Button, Card, SectionHeader, Tag } from "../../../../design-system";
import { InstagramLeadSequence } from "../../../../types/OutreachMetrics";
import {
  controlInstagramLeadSequence,
  listInstagramLeadSequences,
} from "../../../../database/outreach/outreachApi";

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const Sequence = styled.div`
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;

const SequenceHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
`;

const Handle = styled.div`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Meta = styled.div`
  margin-top: 2px;
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-2);
  margin-top: var(--space-3);

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const Action = styled.div`
  padding: var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
`;

const ActionTitle = styled.div`
  text-transform: capitalize;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const ActionTime = styled.div`
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const Controls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  margin-top: var(--space-3);
`;

const Empty = styled.p`
  margin: 0;
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);
`;

const ErrorText = styled.p`
  margin: var(--space-3) 0 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
`;

const sequenceVariant = (status: InstagramLeadSequence["status"]) =>
  status === "completed"
    ? "success"
    : status === "failed" || status === "needs_review"
    ? "danger"
    : status === "running"
    ? "accent"
    : status === "paused"
    ? "warning"
    : "neutral";

const actionVariant = (status: string) =>
  status === "completed" || status === "already_completed"
    ? "success"
    : status === "failed" || status === "ambiguous"
    ? "danger"
    : status === "running"
    ? "accent"
    : "neutral";

const InstagramLeadSequences: React.FC<{ accountId: string }> = ({
  accountId,
}) => {
  const [sequences, setSequences] = useState<InstagramLeadSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSequences(await listInstagramLeadSequences(accountId));
    } catch (e: any) {
      setError(e?.message || "Could not load Instagram sequences.");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => sequences.slice(0, 12), [sequences]);

  const control = async (
    sequence: InstagramLeadSequence,
    command: "pause" | "resume" | "cancel"
  ) => {
    setBusy(`${sequence.id}:${command}`);
    setError(null);
    try {
      const updated = await controlInstagramLeadSequence({
        accountId,
        sequenceId: sequence.id,
        command,
      });
      setSequences((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
    } catch (e: any) {
      setError(e?.message || `Could not ${command} the sequence.`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card padding="lg">
      <SectionHeader
        title="Instagram paced sequences"
        description="Follow → comment → DM, with durable timing and a stop-on-uncertainty safety boundary."
        actions={
          <Button variant="ghost" size="sm" onClick={load} isLoading={loading}>
            Refresh
          </Button>
        }
      />
      {visible.length === 0 ? (
        <Empty>
          {loading
            ? "Loading sequences…"
            : "No paced Instagram sequences have been scheduled for this account."}
        </Empty>
      ) : (
        <List>
          {visible.map((sequence) => (
            <Sequence key={sequence.id}>
              <SequenceHead>
                <div>
                  <Handle>@{sequence.username}</Handle>
                  <Meta>
                    {sequence.id} · created {moment(sequence.createdAt).fromNow()}
                  </Meta>
                </div>
                <Tag variant={sequenceVariant(sequence.status)} size="sm" withDot>
                  {sequence.status.replace(/_/g, " ")}
                </Tag>
              </SequenceHead>
              <Actions>
                {sequence.actions.map((action) => (
                  <Action key={action.id}>
                    <ActionTitle>{action.type}</ActionTitle>
                    <ActionTime>{moment(action.dueAt).format("ddd, MMM D · HH:mm")}</ActionTime>
                    <div style={{ marginTop: "var(--space-2)" }}>
                      <Tag variant={actionVariant(action.status)} size="sm">
                        {action.status.replace(/_/g, " ")}
                      </Tag>
                    </div>
                  </Action>
                ))}
              </Actions>
              {!["completed", "failed", "needs_review", "cancelled"].includes(
                sequence.status
              ) && (
                <Controls>
                  {sequence.status === "paused" ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => control(sequence, "resume")}
                      isLoading={busy === `${sequence.id}:resume`}
                    >
                      Resume
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => control(sequence, "pause")}
                      isLoading={busy === `${sequence.id}:pause`}
                    >
                      Pause
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => control(sequence, "cancel")}
                    isLoading={busy === `${sequence.id}:cancel`}
                  >
                    Cancel
                  </Button>
                </Controls>
              )}
            </Sequence>
          ))}
        </List>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </Card>
  );
};

export default InstagramLeadSequences;
